import React, { useState, useEffect } from "react";
import { BarChart3, Table2, Layers, ShieldCheck, CheckCircle2, PiggyBank,
  CalendarClock, Pencil, Receipt, LogOut } from "lucide-react";
import FilterDropdown from "../components/FilterDropdown";
import { DateField } from "../components/ui/DateField";
import { useAccess } from "../contexts/AccessContext";
import { getDepartmentList } from "../redux/slices/employee";
import { dispatch, useSelector } from "../redux/store";
import { payrollReportsApi, PayrollFilters, PeriodMode } from "../api/salaryReports";
import { toast } from "sonner";

type TabId = "overview" | "register" | "components" | "statutory" | "approvals" | "retainer"
  | "leave" | "adjustments" | "reimbursements" | "fullfinal";

const TABS: { id: TabId; name: string; icon: JSX.Element }[] = [
  { id: "overview", name: "Overview", icon: <BarChart3 className="w-4 h-4" /> },
  { id: "register", name: "Salary Register", icon: <Table2 className="w-4 h-4" /> },
  { id: "components", name: "Components", icon: <Layers className="w-4 h-4" /> },
  { id: "statutory", name: "Statutory", icon: <ShieldCheck className="w-4 h-4" /> },
  { id: "approvals", name: "Approvals", icon: <CheckCircle2 className="w-4 h-4" /> },
  { id: "retainer", name: "Retainer", icon: <PiggyBank className="w-4 h-4" /> },
  { id: "leave", name: "Leave Impact", icon: <CalendarClock className="w-4 h-4" /> },
  { id: "adjustments", name: "Adjustments", icon: <Pencil className="w-4 h-4" /> },
  { id: "reimbursements", name: "Reimbursements", icon: <Receipt className="w-4 h-4" /> },
  { id: "fullfinal", name: "Full & Final", icon: <LogOut className="w-4 h-4" /> },
];

const now = new Date();
const fyLabel = (y: number, m: number) => {
  const s = m >= 4 ? y : y - 1;
  return `${s}-${String(s + 1).slice(-2)}`;
};
const inr = (n: number) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const HEAD_LABEL: Record<string, string> = {
  basicsalary: "Basic", da: "DA", hra: "HRA", conveyance: "Conv", medicalallowance: "Medical",
  lta: "LTA", mealallowance: "Meal", allowance: "Allow", others: "Others", bonus: "Bonus",
  pf: "PF", esi: "ESI", tds: "TDS", proftax: "PT", labourwelfare: "LWF",
};

const DEP_KEYS = (f: PayrollFilters) =>
  [f.mode, f.year, f.month, f.financial_year, f.start_date, f.end_date, f.department];

function useTabData(fetcher: () => Promise<any>, deps: any[]) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetcher()
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, loading };
}

export default function PayrollReports() {
  const { hasAccess, canManage } = useAccess();
  const canPayrollManage = !hasAccess || canManage("payroll-reports");

  const departmentList = useSelector((s: any) => s.employee.departmentList);
  useEffect(() => { if (canPayrollManage) dispatch(getDepartmentList()); }, [canPayrollManage]);

  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState<PayrollFilters>({
    mode: "month", year: now.getFullYear(), month: now.getMonth() + 1,
    financial_year: fyLabel(now.getFullYear(), now.getMonth() + 1),
  });

  const setMode = (mode: PeriodMode) => setFilters((f) => ({ ...f, mode }));
  const patch = (p: Partial<PayrollFilters>) => setFilters((f) => ({ ...f, ...p }));

  const exportErr = async (e: any, fallback: string) => {
    let msg = fallback;
    const blob = e?.response?.data;
    if (blob instanceof Blob) { try { msg = JSON.parse(await blob.text())?.detail || msg; } catch {} }
    return msg;
  };
  const onExportCsv = async () => {
    try { await payrollReportsApi.exportCsv("register", filters); toast.success("CSV downloaded"); }
    catch (e: any) { toast.error(await exportErr(e, "CSV export failed")); }
  };
  const onExportPdf = async () => {
    try { await payrollReportsApi.exportPdf(filters); toast.success("PDF downloaded"); }
    catch (e: any) { toast.error(await exportErr(e, "PDF export failed")); }
  };

  return (
    <div className="gt-themed gt-shell min-h-screen p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="gt-page-title">Payroll Reports</h1>
          <p className="gt-page-sub">
            {canPayrollManage
              ? "Deep tracking across every salary head, deduction, approval and retainer."
              : "Your salary, deductions, retainer and approvals."}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onExportCsv} className="gt-action-btn" style={{ whiteSpace: "nowrap" }}>
            Export CSV
          </button>
          <button onClick={onExportPdf} className="gt-action-btn" style={{ whiteSpace: "nowrap" }}>
            Export PDF
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 mb-4 overflow-hidden">
        <button type="button" onClick={() => setShowFilters((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50">
          <span className="text-sm font-medium text-gray-800">Filters</span>
          <span className="text-gray-500">{showFilters ? "▲" : "▼"}</span>
        </button>
        {showFilters && (
          <div className="px-4 pb-4 border-t border-gray-100 pt-4">
            <div className="flex gap-2 mb-4">
              {(["month", "fy", "range"] as PeriodMode[]).map((m) => (
                <button key={m} onClick={() => setMode(m)}
                  className="px-3 py-1.5 rounded-lg text-sm"
                  style={{ border: "1px solid var(--gt-border)",
                    background: filters.mode === m ? "var(--gt-primary)" : "transparent",
                    color: filters.mode === m ? "#fff" : "var(--gt-text-secondary)" }}>
                  {m === "month" ? "Monthly" : m === "fy" ? "Financial Year" : "Date Range"}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              {filters.mode === "month" && (<>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <FilterDropdown value={String(filters.month)} onChange={(v) => patch({ month: Number(v) })} searchable={false} ariaLabel="Month" options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: new Date(2000, i, 1).toLocaleString("en", { month: "long" }) }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <FilterDropdown value={String(filters.year)} onChange={(v) => patch({ year: Number(v) })} searchable={false} ariaLabel="Year" options={[0, 1, 2, 3].map((d) => { const y = now.getFullYear() - d; return { value: String(y), label: String(y) }; })} />
                </div>
              </>)}
              {filters.mode === "fy" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Financial Year</label>
                  <FilterDropdown value={filters.financial_year || ""} onChange={(v) => patch({ financial_year: v })} searchable={false} ariaLabel="Financial Year" options={[0, 1, 2].map((d) => { const lbl = fyLabel(now.getFullYear() - d, now.getMonth() + 1); return { value: lbl, label: lbl }; })} />
                </div>
              )}
              {filters.mode === "range" && (<>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                  <DateField value={filters.start_date || ""} onChange={(v) => patch({ start_date: v })} placeholder="Start" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                  <DateField value={filters.end_date || ""} onChange={(v) => patch({ end_date: v })} placeholder="End" />
                </div>
              </>)}
              {canPayrollManage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <FilterDropdown value={filters.department || "all"} onChange={(v) => patch({ department: v === "all" ? undefined : v })} ariaLabel="Department" options={[{ value: "all", label: "All departments" }, ...(departmentList || []).map((d: any) => ({ value: d.name, label: d.name }))]} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: "1px solid var(--gt-border)", marginBottom: 24 }}>
        <nav className="flex gap-1 flex-nowrap overflow-x-auto" style={{ scrollbarWidth: "none" }}>
          {TABS.map((t) => {
            const active = activeTab === t.id;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, display: "flex", gap: 6, alignItems: "center",
                  borderBottom: active ? "2px solid var(--gt-primary)" : "2px solid transparent",
                  color: active ? "var(--gt-primary-deep)" : "var(--gt-text-tertiary)" }}>
                {t.icon}{t.name}
              </button>
            );
          })}
        </nav>
      </div>

      <div>
        {activeTab === "overview" && <OverviewTab filters={filters} />}
        {activeTab === "register" && <RegisterTab filters={filters} />}
        {activeTab === "components" && <ComponentsTab filters={filters} />}
        {activeTab === "statutory" && <StatutoryTab filters={filters} />}
        {activeTab === "approvals" && <ApprovalsTab filters={filters} />}
        {activeTab === "retainer" && <RetainerTab />}
        {activeTab === "leave" && <LeaveImpactTab filters={filters} />}
        {activeTab === "adjustments" && <AdjustmentsTab filters={filters} />}
        {activeTab === "reimbursements" && <ReimbursementsTab filters={filters} />}
        {activeTab === "fullfinal" && <FullFinalTab filters={filters} />}
      </div>
    </div>
  );
}

// ───────────────────────── Overview ─────────────────────────

function OverviewTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading } = useTabData(() => payrollReportsApi.overview(filters), DEP_KEYS(filters));
  const [openDept, setOpenDept] = useState<string | null>(null);
  const [regRows, setRegRows] = useState<any[] | null>(null);
  const [regLoading, setRegLoading] = useState(false);

  // Reset the drill-down whenever the period/department filter changes.
  useEffect(() => { setOpenDept(null); setRegRows(null); }, DEP_KEYS(filters));

  if (loading) return <div className="gt-card p-5">Loading…</div>;
  if (!data) return <div className="gt-card p-5">No payroll data for this period.</div>;
  const t = data.totals;
  const tiles = [
    { label: "Net Payout", value: inr(t.net_payout) },
    { label: "Headcount Paid", value: t.headcount },
    { label: "Bonus + Extra", value: inr(t.bonus + t.extrapay) },
    { label: "Retainer Held", value: inr(t.retainer_withheld) },
    { label: "Pending Approvals", value: t.pending_approvals },
  ];
  const byDept = data.charts.by_department || [];
  const max = Math.max(1, ...byDept.map((d: any) => d.net));

  const toggleDept = async (dept: string) => {
    if (openDept === dept) { setOpenDept(null); return; }
    setOpenDept(dept);
    if (regRows === null) {
      setRegLoading(true);
      try { const r = await payrollReportsApi.register(filters); setRegRows(r.data.rows || []); }
      catch { setRegRows([]); }
      finally { setRegLoading(false); }
    }
  };
  const empsFor = (dept: string) =>
    (regRows || [])
      .filter((r: any) => (r.department || "Unassigned") === dept)
      .sort((a: any, b: any) => (b.net_paid || 0) - (a.net_paid || 0));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {tiles.map((m) => (
          <div key={m.label} className="gt-card p-5">
            <span className="gt-eyebrow">{m.label}</span>
            <p className="gt-display gt-numerals" style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>{m.value}</p>
          </div>
        ))}
      </div>
      <div className="gt-card p-5">
        <span className="gt-eyebrow">Net Payout by Department</span>
        <p className="gt-page-sub" style={{ fontSize: 11, marginTop: 2 }}>Click a department to see who's in it and how much each.</p>
        <div className="mt-4 space-y-2">
          {byDept.length === 0 && <p className="gt-page-sub">No breakdown.</p>}
          {byDept.map((d: any) => {
            const isOpen = openDept === d.department;
            const emps = isOpen ? empsFor(d.department) : [];
            return (
              <div key={d.department}>
                <div onClick={() => toggleDept(d.department)} className="flex items-center gap-3"
                  style={{ cursor: "pointer" }}>
                  <span style={{ width: 140, fontSize: 12, display: "flex", alignItems: "center", gap: 4 }} className="truncate">
                    <span style={{ display: "inline-block", transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s", color: "var(--gt-text-tertiary)" }}>▸</span>
                    {d.department}
                  </span>
                  <div style={{ flex: 1, background: "var(--gt-surface-2)", borderRadius: 6, height: 18 }}>
                    <div style={{ width: `${(d.net / max) * 100}%`, background: "var(--gt-primary)", height: 18, borderRadius: 6 }} />
                  </div>
                  <span style={{ width: 110, textAlign: "right", fontSize: 12 }}>{inr(d.net)}</span>
                </div>
                {isOpen && (
                  <div style={{ margin: "6px 0 10px 20px", paddingLeft: 12, borderLeft: "2px solid var(--gt-border)" }}>
                    {regLoading && regRows === null ? (
                      <p className="gt-page-sub" style={{ fontSize: 12 }}>Loading…</p>
                    ) : emps.length === 0 ? (
                      <p className="gt-page-sub" style={{ fontSize: 12 }}>No employees paid in this department.</p>
                    ) : (
                      <table style={{ width: "100%", fontSize: 12 }}>
                        <tbody>
                          {emps.map((e: any) => (
                            <tr key={e.emptID}>
                              <td style={{ padding: "3px 0" }}>
                                {e.name} <span className="gt-page-sub">· {e.designation || "—"}</span>
                              </td>
                              <td style={{ padding: "3px 0", textAlign: "right", fontWeight: 600 }}>{inr(e.net_paid)}</td>
                            </tr>
                          ))}
                          <tr style={{ borderTop: "1px solid var(--gt-border)" }}>
                            <td style={{ padding: "3px 0", color: "var(--gt-text-tertiary)" }}>{emps.length} employee{emps.length > 1 ? "s" : ""}</td>
                            <td style={{ padding: "3px 0", textAlign: "right", fontWeight: 800 }}>{inr(d.net)}</td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── Salary Register ─────────────────────────

function RegisterTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading } = useTabData(() => payrollReportsApi.register(filters), DEP_KEYS(filters));
  const [open, setOpen] = useState<string | null>(null);
  const [daily, setDaily] = useState<any>(null);

  if (loading) return <div className="gt-card p-5">Loading…</div>;
  if (!data || data.rows.length === 0) return <div className="gt-card p-5">No salary records for this period.</div>;

  const earn = data.columns.earnings as string[];
  const ded = data.columns.deductions as string[];

  const toggle = async (r: any) => {
    const key = `${r.emptID}-${r.year}-${r.month}`;
    if (open === key) { setOpen(null); return; }
    setOpen(key); setDaily(null);
    try {
      const res = await payrollReportsApi.registerDaily(r.emptID, r.year, r.month);
      setDaily(res.data);
    } catch { setDaily({ attendance: [] }); }
  };

  return (
    <div className="gt-card p-0" style={{ overflow: "auto", maxHeight: "calc(100vh - 80px)" }}>
      <table style={{ minWidth: 1100, width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "var(--gt-surface-2)", textAlign: "right" }}>
            <th style={{ textAlign: "left", padding: 8, position: "sticky", left: 0, top: 0, zIndex: 3, background: "var(--gt-surface-2)" }}>Employee</th>
            {earn.map((h) => <th key={h} style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>{HEAD_LABEL[h] || h}</th>)}
            {ded.map((h) => <th key={h} style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)", color: "#b00" }}>{HEAD_LABEL[h] || h}</th>)}
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Retainer</th>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)", fontWeight: 800 }}>Net Paid</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r: any) => {
            const key = `${r.emptID}-${r.year}-${r.month}`;
            return (
              <React.Fragment key={key}>
                <tr onClick={() => toggle(r)} style={{ cursor: "pointer", textAlign: "right", borderTop: "1px solid var(--gt-border)" }}>
                  <td style={{ textAlign: "left", padding: 8, position: "sticky", left: 0, background: "var(--gt-surface-0)" }}>
                    {r.name} <span className="gt-page-sub">· {r.department}</span>
                  </td>
                  {earn.map((h) => <td key={h} style={{ padding: 8 }}>{r[h] ? Number(r[h]).toLocaleString("en-IN") : "—"}</td>)}
                  {ded.map((h) => <td key={h} style={{ padding: 8, color: "#b00" }}>{r[h] ? Number(r[h]).toLocaleString("en-IN") : "—"}</td>)}
                  <td style={{ padding: 8 }}>{r.retainer_withheld ? Number(r.retainer_withheld).toLocaleString("en-IN") : "—"}</td>
                  <td style={{ padding: 8, fontWeight: 800 }}>{inr(r.net_paid)}</td>
                </tr>
                {open === key && (
                  <tr><td colSpan={earn.length + ded.length + 3} style={{ padding: 12, background: "var(--gt-surface-1)" }}>
                    {!daily ? "Loading daily…" : (daily.attendance?.length ? (
                      <table style={{ width: "100%", fontSize: 11 }}>
                        <thead><tr style={{ textAlign: "left" }}>
                          <th>Date</th><th>Status</th><th>Hours</th><th>Day Salary</th><th>OT</th>
                        </tr></thead>
                        <tbody>
                          {daily.attendance.map((a: any, i: number) => (
                            <tr key={i}><td>{a.date}</td><td>{a.status}</td><td>{a.hours}</td>
                              <td>{inr(a.day_salary)}</td><td>{inr(a.overtime_amount)}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    ) : "No daily breakdown stored for this row.")}
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: "2px solid var(--gt-border)", textAlign: "right", fontWeight: 700 }}>
            <td style={{ textAlign: "left", padding: 8 }}>Totals</td>
            {earn.map((h) => <td key={h} style={{ padding: 8 }}>{Number(data.totals[h] || 0).toLocaleString("en-IN")}</td>)}
            {ded.map((h) => <td key={h} style={{ padding: 8 }}>{Number(data.totals[h] || 0).toLocaleString("en-IN")}</td>)}
            <td style={{ padding: 8 }}>{Number(data.totals.retainer_withheld || 0).toLocaleString("en-IN")}</td>
            <td style={{ padding: 8 }}>{inr(data.totals.net_paid)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ───────────────────────── Components ─────────────────────────

function ComponentsTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading } = useTabData(() => payrollReportsApi.components(filters), DEP_KEYS(filters));
  if (loading) return <div className="gt-card p-5">Loading…</div>;
  if (!data || data.employees.length === 0) return <div className="gt-card p-5">No salary structures found.</div>;
  return (
    <div className="space-y-4">
      {data.employees.map((e: any) => (
        <div key={e.emptID} className="gt-card p-5">
          <div className="flex justify-between mb-3">
            <strong>{e.name}</strong><span className="gt-page-sub">{e.designation} · {e.department}</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 12 }}>
              <thead><tr style={{ textAlign: "left", color: "var(--gt-text-tertiary)" }}>
                <th>FY</th><th>Effective</th><th>End</th><th>CTC</th><th>Basic</th><th>HRA</th>
                <th>PF</th><th>TDS</th><th>PF Rate</th><th>Declared</th>
              </tr></thead>
              <tbody>
                {e.history.map((h: any) => (
                  <tr key={h.id} style={{ borderTop: "1px solid var(--gt-border)" }}>
                    <td>{h.financial_year || "—"}</td><td>{h.effective_date || "—"}</td><td>{h.end_date || "current"}</td>
                    <td>{inr(h.ctc)}</td><td>{inr(h.basicsalary)}</td><td>{inr(h.hra)}</td>
                    <td>{inr(h.pf)}</td><td>{inr(h.tds)}</td>
                    <td>{h.pf_employee_rate ? `${h.pf_employee_rate}%${h.pf_rate_locked ? " 🔒" : ""}` : "—"}</td>
                    <td>{h.declaration_locked ? "Locked" : "Open"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ───────────────────────── Statutory ─────────────────────────

function StatutoryTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading } = useTabData(() => payrollReportsApi.statutory(filters), DEP_KEYS(filters));
  if (loading) return <div className="gt-card p-5">Loading…</div>;
  if (!data || data.rows.length === 0) return <div className="gt-card p-5">No statutory data for this period.</div>;
  const heads = data.heads as string[];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {heads.map((h) => (
          <div key={h} className="gt-card p-5">
            <span className="gt-eyebrow">{HEAD_LABEL[h] || h}</span>
            <p className="gt-display gt-numerals" style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{inr(data.totals[h])}</p>
          </div>
        ))}
      </div>
      <div className="gt-card p-0" style={{ overflow: "auto", maxHeight: "calc(100vh - 80px)" }}>
        <table style={{ width: "100%", fontSize: 12 }}>
          <thead><tr style={{ background: "var(--gt-surface-2)", textAlign: "right" }}>
            <th style={{ textAlign: "left", padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Employee</th>
            {heads.map((h) => <th key={h} style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>{HEAD_LABEL[h] || h}</th>)}
          </tr></thead>
          <tbody>
            {data.rows.map((r: any) => (
              <tr key={r.emptID} style={{ borderTop: "1px solid var(--gt-border)", textAlign: "right" }}>
                <td style={{ textAlign: "left", padding: 8 }}>{r.name}</td>
                {heads.map((h) => <td key={h} style={{ padding: 8 }}>{r[h] ? Number(r[h]).toLocaleString("en-IN") : "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────── Approvals ─────────────────────────

function ApprovalsTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading } = useTabData(() => payrollReportsApi.approvals(filters),
    [filters.mode, filters.year, filters.month, filters.financial_year, filters.start_date, filters.end_date]);
  if (loading) return <div className="gt-card p-5">Loading…</div>;
  if (!data) return <div className="gt-card p-5">No approval data.</div>;
  const pill = (s: string) => (({
    PENDING: { background: "#fef3c7", color: "#92400e" },
    APPROVED: { background: "#dcfce7", color: "#166534" },
    REJECTED: { background: "#fee2e2", color: "#991b1b" },
  } as any)[s] || {});
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {(["PENDING", "APPROVED", "REJECTED"]).map((k) => (
          <div key={k} className="gt-card p-5">
            <span className="gt-eyebrow">{k}</span>
            <p className="gt-display gt-numerals" style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>{data.counts[k] || 0}</p>
          </div>
        ))}
      </div>
      <div className="gt-card p-0" style={{ overflow: "auto", maxHeight: "calc(100vh - 80px)" }}>
        <table style={{ width: "100%", fontSize: 12 }}>
          <thead><tr style={{ background: "var(--gt-surface-2)", textAlign: "left" }}>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Employee</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Period</th>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Status</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Approved By</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>At</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r: any, i: number) => (
              <tr key={i} style={{ borderTop: "1px solid var(--gt-border)" }}>
                <td style={{ padding: 8 }}>{r.name}</td>
                <td style={{ padding: 8 }}>{r.month}/{r.year}</td>
                <td style={{ padding: 8 }}><span style={{ ...pill(r.status), padding: "2px 8px", borderRadius: 12, fontSize: 11 }}>{r.status}</span></td>
                <td style={{ padding: 8 }}>
                  {r.approved_by_name ? (
                    <>
                      {r.approved_by_name}
                      {r.approved_by_role ? (
                        <span style={{ color: "var(--gt-text-tertiary)", marginLeft: 6, textTransform: "capitalize" }}>
                          ({r.approved_by_role})
                        </span>
                      ) : null}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td style={{ padding: 8 }}>{r.approved_at ? new Date(r.approved_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────── Retainer ─────────────────────────

function RetainerTab() {
  const { data, loading } = useTabData(() => payrollReportsApi.retainer(), []);
  if (loading) return <div className="gt-card p-5">Loading…</div>;
  if (!data || data.employees.length === 0) return <div className="gt-card p-5">No retainer configs.</div>;
  return (
    <div className="space-y-4">
      {data.employees.map((e: any) => (
        <div key={e.emptID} className="gt-card p-5">
          <div className="flex justify-between mb-2">
            <strong>{e.name}</strong>
            <span>Outstanding pool: <b>{inr(e.outstanding)}</b></span>
          </div>
          <p className="gt-page-sub mb-3">
            ₹{e.config?.amount}/mo · {e.config?.period_months}-month cycle · {e.config?.recurring ? "recurring" : "one-time"} · {e.config?.status}
          </p>
          <table style={{ width: "100%", fontSize: 12 }}>
            <thead><tr style={{ textAlign: "right", color: "var(--gt-text-tertiary)" }}>
              <th style={{ textAlign: "left" }}>Period</th><th>Kind</th><th>Withheld</th><th>Released</th><th>Balance</th>
            </tr></thead>
            <tbody>
              {e.ledger.map((l: any, i: number) => (
                <tr key={i} style={{ textAlign: "right", borderTop: "1px solid var(--gt-border)" }}>
                  <td style={{ textAlign: "left" }}>{l.month ? `${l.month}/${l.year}` : "exit"}</td>
                  <td>{l.kind}</td><td>{inr(l.withheld)}</td><td>{inr(l.released)}</td><td>{inr(l.balance_after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ───────────────────────── Leave Impact ─────────────────────────

function LeaveImpactTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading } = useTabData(() => payrollReportsApi.leaveImpact(filters), DEP_KEYS(filters));
  if (loading) return <div className="gt-card p-5">Loading…</div>;
  if (!data || data.rows.length === 0) return <div className="gt-card p-5">No approved leave in this period.</div>;
  return (
    <div className="space-y-4">
      {data.rows.map((r: any) => (
        <div key={r.emptID} className="gt-card p-5">
          <div className="flex justify-between mb-2">
            <strong>{r.name} <span className="gt-page-sub">· {r.department}</span></strong>
            <span>Total leave: <b>{r.total_days} days</b></span>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(r.by_type || {}).map(([t, d]: any) => (
              <span key={t} style={{ background: "var(--gt-surface-2)", borderRadius: 12, padding: "2px 10px", fontSize: 12 }}>
                {t}: {d}d
              </span>
            ))}
          </div>
          {r.balances?.length > 0 && (
            <table style={{ width: "100%", fontSize: 12 }}>
              <thead><tr style={{ textAlign: "right", color: "var(--gt-text-tertiary)" }}>
                <th style={{ textAlign: "left" }}>Leave Type</th><th>Opening</th><th>Used</th><th>Closing</th>
              </tr></thead>
              <tbody>
                {r.balances.map((b: any, i: number) => (
                  <tr key={i} style={{ textAlign: "right", borderTop: "1px solid var(--gt-border)" }}>
                    <td style={{ textAlign: "left" }}>{b.leave_type}</td><td>{b.opening}</td><td>{b.used}</td><td>{b.closing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

// ───────────────────────── Adjustments Audit ─────────────────────────

function AdjustmentsTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading } = useTabData(() => payrollReportsApi.adjustments(filters), DEP_KEYS(filters));
  if (loading) return <div className="gt-card p-5">Loading…</div>;
  if (!data || data.rows.length === 0) return <div className="gt-card p-5">No manual adjustments in this period.</div>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="gt-card p-5"><span className="gt-eyebrow">Total Bonus</span>
          <p className="gt-display gt-numerals" style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{inr(data.total_bonus)}</p></div>
        <div className="gt-card p-5"><span className="gt-eyebrow">Total Extra Pay</span>
          <p className="gt-display gt-numerals" style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{inr(data.total_extrapay)}</p></div>
      </div>
      <div className="gt-card p-0" style={{ overflow: "auto", maxHeight: "calc(100vh - 80px)" }}>
        <table style={{ width: "100%", fontSize: 12 }}>
          <thead><tr style={{ background: "var(--gt-surface-2)", textAlign: "left" }}>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Employee</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Period</th>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Bonus</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Extra Pay</th>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Net After</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>When</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Description</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r: any, i: number) => (
              <tr key={i} style={{ borderTop: "1px solid var(--gt-border)" }}>
                <td style={{ padding: 8 }}>{r.name}</td>
                <td style={{ padding: 8 }}>{r.month}/{r.year}</td>
                <td style={{ padding: 8 }}>{inr(r.bonus)}</td>
                <td style={{ padding: 8 }}>{inr(r.extrapay)}</td>
                <td style={{ padding: 8 }}>{inr(r.final_net_salary)}</td>
                <td style={{ padding: 8 }}>{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}</td>
                <td style={{ padding: 8 }}>{r.description || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────── Reimbursements ─────────────────────────

function ReimbursementsTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading } = useTabData(() => payrollReportsApi.reimbursements(filters), DEP_KEYS(filters));
  if (loading) return <div className="gt-card p-5">Loading…</div>;
  if (!data || data.rows.length === 0) return <div className="gt-card p-5">No approved reimbursements in this period.</div>;
  return (
    <div className="space-y-4">
      <div className="gt-card p-5"><span className="gt-eyebrow">Total Reimbursed</span>
        <p className="gt-display gt-numerals" style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>{inr(data.total_reimbursed)}</p></div>
      <div className="gt-card p-0" style={{ overflow: "auto", maxHeight: "calc(100vh - 80px)" }}>
        <table style={{ width: "100%", fontSize: 12 }}>
          <thead><tr style={{ background: "var(--gt-surface-2)", textAlign: "left" }}>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Employee</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Claim</th>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Amount</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Status</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Submitted</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r: any) => (
              <tr key={r.id} style={{ borderTop: "1px solid var(--gt-border)" }}>
                <td style={{ padding: 8 }}>{r.name}</td>
                <td style={{ padding: 8 }}>{r.title || "—"}</td>
                <td style={{ padding: 8 }}>{inr(r.amount)}</td>
                <td style={{ padding: 8 }}>{r.status}</td>
                <td style={{ padding: 8 }}>{r.submitted_on ? new Date(r.submitted_on).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────── Full & Final ─────────────────────────

function FullFinalTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading } = useTabData(() => payrollReportsApi.fullFinal(filters), DEP_KEYS(filters));
  if (loading) return <div className="gt-card p-5">Loading…</div>;
  if (!data || data.rows.length === 0) return <div className="gt-card p-5">No full &amp; final settlements in this period.</div>;
  return (
    <div className="space-y-4">
      <div className="gt-card p-5"><span className="gt-eyebrow">Total Net Payable</span>
        <p className="gt-display gt-numerals" style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>{inr(data.total_net_payable)}</p></div>
      {data.rows.map((s: any) => (
        <div key={s.id} className="gt-card p-5">
          <div className="flex justify-between mb-3">
            <strong>{s.name} <span className="gt-page-sub">· {s.separation_type}</span></strong>
            <span style={{ fontSize: 12 }}>{s.status}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" style={{ fontSize: 12 }}>
            <div><span className="gt-page-sub">Pending Salary</span><div>{inr(s.pending_salary)}</div></div>
            <div><span className="gt-page-sub">Leave Encashment</span><div>{inr(s.leave_encashment)}</div></div>
            <div><span className="gt-page-sub">Gratuity</span><div>{inr(s.gratuity)}</div></div>
            <div><span className="gt-page-sub">Bonus Due</span><div>{inr(s.bonus_due)}</div></div>
            <div><span className="gt-page-sub">Gross Earnings</span><div>{inr(s.gross_earnings)}</div></div>
            <div><span className="gt-page-sub">Total Deductions</span><div style={{ color: "#b00" }}>{inr(s.total_deductions)}</div></div>
            <div><span className="gt-page-sub">Net Payable</span><div style={{ fontWeight: 800 }}>{inr(s.net_payable)}</div></div>
          </div>
        </div>
      ))}
    </div>
  );
}
